import { Users, ChevronRight, Check, Clock } from 'lucide-react';
import { RatingFactorsChip, type RatingFactorsData } from '@/components/duels/RatingFactorsChip';
import type { DuelCardState } from './types';

interface ResultInfo {
  label: string;
  ratingChange: string;
  bgColor: string;
  textColor: string;
  borderColor: string;
}

interface DuelCardHeaderProps {
  totalVotes: number;
  ratingFactors?: RatingFactorsData | null;
  state: DuelCardState;
  showResultFooter: boolean;
  resultInfo: ResultInfo | null;
}

function getStateBadge(state: DuelCardState) {
  const activeBadgeClass =
    'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-bold border bg-success/10 text-success border-success/30';
  const lockedBadgeClass =
    'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold border bg-muted/50 text-muted-foreground border-border';
  const partialBadgeClass =
    'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold border bg-warning/10 text-warning border-warning/30';

  switch (state) {
    case 'active':
      return <span className={activeBadgeClass}>Active</span>;
    case 'locked':
      return (
        <span className={lockedBadgeClass}>
          <Clock className="h-3 w-3" />
          Locked
        </span>
      );
    case 'partial':
      return (
        <span className={partialBadgeClass}>
          <Clock className="h-3 w-3" />
          In progress
        </span>
      );
    case 'resolved':
      return (
        <span className={`${activeBadgeClass} gap-1 min-h-[26px]`}>
          <Check className="h-3 w-3" />
          Resolved
        </span>
      );
    case 'cancelled':
      return <span className={lockedBadgeClass}>Cancelled</span>;
    default:
      return null;
  }
}

export function DuelCardHeader({
  totalVotes,
  ratingFactors,
  state,
  showResultFooter,
  resultInfo,
}: DuelCardHeaderProps) {
  return (
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center gap-1.5 flex-wrap">
        <Users className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">
          <span className="text-sm font-bold">{totalVotes}</span>{' '}
          <span className="font-bold">votes</span>
        </span>
        <ChevronRight className="h-2.5 w-2.5 text-muted-foreground" />
        {ratingFactors && <RatingFactorsChip data={ratingFactors} className="ml-0.5" />}
      </div>
      <div className="flex items-center gap-1.5">
        {showResultFooter && resultInfo ? (
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-sm font-bold border ${resultInfo.bgColor} ${resultInfo.textColor} ${resultInfo.borderColor}`}
          >
            {resultInfo.label}
            <span className="mx-1.5 opacity-70">|</span>
            {resultInfo.ratingChange}
          </span>
        ) : (
          getStateBadge(state)
        )}
      </div>
    </div>
  );
}
